import { useFormContext } from 'react-hook-form'

import { format, isAfter } from 'date-fns'
import { de } from 'date-fns/locale'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { BookExtendForm } from '@/pages/customer/extend-booking'
import { toZonedTime } from 'date-fns-tz'

interface CurrentBookingFormProps {
	currentBookings: UserBooking[] | undefined
}
export const CurrentBookingForm = ({ currentBookings }: CurrentBookingFormProps) => {
	const form = useFormContext<BookExtendForm>()

	return (
		<div className="mt-[30px] lg:mt-12 flex flex-col">
			<h5 className="text-secondary/50 max-lg:text-xs font-medium">
				Bitte wähle das Regal aus, welches du verlängern möchtest.
			</h5>
			{currentBookings && currentBookings.length === 0 && (
				<p className="mt-5 lg:mt-6 lg:text-lg">
					Du hast derzeit keine laufenden oder anstehenden Buchungen, die verlängert werden können.
				</p>
			)}
			<div className="mt-5 lg:mt-6 grid grid-cols-1 lg:grid-cols-2 items-stretch gap-y-5 lg:gap-y-8 gap-x-[23px]">
				{currentBookings?.map(booking => (
					<div
						key={booking.id}
						onClick={() => {
							if (booking.rent_price === 0 && booking.original_price === 0) return
							form.setValue('bookingId', booking.id.toString())
						}}
						className={cn(
							'rounded-[16px] group hover:border-3 hover:border-primary/20 hover:ring hover:ring-primary cursor-pointer border border-[#E7E9ED] p-4 lg:p-6 flex flex-col gap-5 lg:gap-8',
							{
								'border-3 border-primary/20 ring ring-primary':
									Number(form.watch('bookingId')) === booking.id,
								'opacity-50 hover:border-[#E7E9ED] hover:border-1 hover:ring-0 cursor-not-allowed':
									booking.rent_price === 0 && booking.original_price === 0,
							},
						)}>
						<h1 className="text-secondary font-extrabold text-xs lg:text-xl">Details</h1>
						<div className="flex flex-col gap-3 lg:gap-4">
							<div className="flex items-center justify-between pb-3 lg:pb-4 border-b border-[#E7E9ED]">
								<p className="text-secondary/50 font-medium max-lg:text-xs">Buchungsdauen</p>
								<p className="text-secondary font-extrabold max-lg:text-xs">
									{booking.duration} Tage
								</p>
							</div>
							<div className="flex items-center justify-between pb-3 lg:pb-4 border-b border-[#E7E9ED]">
								<p className="text-secondary/50 font-medium max-lg:text-xs">Mietbeginn</p>
								<p className="text-secondary font-extrabold max-lg:text-xs">
									{format(toZonedTime(new Date(booking.start), 'UTC'), 'dd. MMMM yyyy', {
										locale: de,
									})}
								</p>
							</div>
							<div className="flex items-center justify-between pb-3 lg:pb-4 border-b border-[#E7E9ED]">
								<p className="text-secondary/50 font-medium max-lg:text-xs">Mietende</p>
								<p className="text-secondary font-extrabold max-lg:text-xs">
									{format(toZonedTime(new Date(booking.end), 'UTC'), 'dd. MMMM yyyy', {
										locale: de,
									})}
								</p>
							</div>
							<div className="flex items-center justify-between pb-3 lg:pb-4 border-b border-[#E7E9ED]">
								<p className="text-secondary/50 font-medium max-lg:text-xs">Regal-Nummer</p>
								<p className="text-secondary font-extrabold max-lg:text-xs">{booking.stand_id}</p>
							</div>
							<div className="flex items-center justify-between">
								<p className="text-secondary/50 font-medium max-lg:text-xs">Status</p>
								<p
									className={cn(
										'w-12 lg:w-[81px] h-6 lg:h-8 text-xs lg:text-sm font-medium rounded-[18px] flex items-center justify-center',
										isAfter(toZonedTime(new Date(booking.start), 'UTC'), new Date())
											? 'bg-blue-100 text-blue-600'
											: 'bg-[#61B9641A] text-[#61B964]',
									)}>
									{isAfter(toZonedTime(new Date(booking.start), 'UTC'), new Date())
										? 'Offen'
										: 'Aktiv'}
								</p>
							</div>
						</div>
						<Button
							variant="outline"
							type="button"
							disabled={booking.rent_price === 0 && booking.original_price === 0}
							className={cn(
								'rounded-lg h-12 text-primary hover:text-white group-hover:text-white hover:bg-primary group-hover:bg-primary group-hover:border-0 font-extrabold',
								{
									'border-0 bg-primary text-white': Number(form.watch('bookingId')) === booking.id,
									'cursor-not-allowed group-hover:bg-white group-hover:text-primary group-hover:border-1 hover:bg-white hover:text-primary':
										booking.rent_price === 0 && booking.original_price === 0,
								},
							)}>
							Jetzt verlängern
						</Button>
					</div>
				))}
			</div>
		</div>
	)
}
